<template>
  <div class="form-row">
    <div class="col-md-3">
      <div class="card" v-if="sipUser">
        <div class="card-body text-center">
          <img
            v-if="sipUser.Email"
            :src="`${$http.defaults.baseURL}/Document/download/Employees/${sipUser.Email}.jpg`"
            class="rounded-circle img-fluid w-50"
            @error="replaceByDefault"
          />
          <img
            v-else
            src="/img/avatar.png"
            class="rounded-circle img-fluid w-50"
          />
        </div>
        <div class="card-body p-2 text-center">
          <h5 class="card-title">{{ sipUser.FullName }}</h5>
          <p class="card-text">
            {{ $const.SIP_ROLES_TEXT[sipUser.RoleCode] }}
            <span v-if="sipUser.AreaName"> in {{ sipUser.AreaName }} </span>
          </p>
        </div>
        <ul class="list-group list-group-flush">
          <li class="list-group-item">
            <i class="fas fa-envelope mr-2"></i>
            <a :href="`mailto:${sipUser.Email}`">{{ sipUser.Email }}</a>
          </li>
          <li class="list-group-item" v-if="sipUser.Company">
            <i class="fas fa-building mr-2"></i>
            {{ sipUser.Company }}
          </li>
        </ul>
      </div>
    </div>

    <div class="col-md-9">
      <button
        v-if="hasNewNotice"
        class="new-notice btn btn-sm btn-primary rounded-pill"
        @click="reloadPage"
      >
        <i class="fas fa-arrow-up"></i>
        New Notice
      </button>
      <div class="border border-gray p-3 bg-white rounded box-shadow">
        <div
          class="d-flex justify-content-between align-items-center w-100 border-bottom border-gray pb-2"
        >
          <h5 class="mb-0">
            Notifications
            <div class="d-inline-block font-italic" v-if="loading">
              <CSpinner color="info" size="sm" class="ml-2" />
              <small>Loading...</small>
            </div>
            <a v-else href="javascript:" @click="reloadPage">
              <i class="fas fa-refresh"></i>
            </a>
          </h5>
          <a href="javascript:" class="text-muted" @click="markAllAsRead">
            Mark all as read
          </a>
        </div>

        <template v-if="items && items.length">
          <div
            v-for="item in items"
            :key="`notice-${item.Id}`"
            class="media text-muted pt-2 px-2 notice-item"
            :class="{ 'bg-light': item.Status == $const.NOTICE_STATUS.New }"
          >
            <img
              class="mr-2 rounded-circle"
              style="width: 50px; height: 50px"
              v-if="item.CreatedByNavigation && item.CreatedByNavigation.Email"
              :src="`${$http.defaults.baseURL}/Document/download/Employees/${item.CreatedByNavigation.Email}.jpg`"
            />
            <img v-else src="/img/avatar.png" class="mr-2 rounded-circle" />

            <div class="media-body pb-2 mb-0 lh-125 border-bottom border-gray">
              <div
                class="d-flex justify-content-between align-items-center w-100"
              >
                <div role="button" @click="clickNotice(item)">
                  <div v-html="showNoticeText(item)"></div>
                  <span class="text-muted small">
                    <i class="fas fa-clock"></i>
                    {{ showDateTime(item.CreatedAt) }}
                  </span>
                </div>
                <div class="d-flex justify-content-between align-items-center">
                  <i
                    v-if="item.Status == $const.NOTICE_STATUS.New"
                    class="fas fa-circle"
                    style="font-size: 0.8rem"
                  ></i>
                  <CDropdown
                    class="c-header-nav-items"
                    placement="bottom-end"
                    add-menu-classes="py-0"
                  >
                    <template #toggler>
                      <CButton
                        color="none"
                        size="sm"
                        class="float-right d-inline"
                        title="Options"
                        style="font-size: 1.2rem; line-height: 1rem"
                      >
                        <i class="fas fa-ellipsis"></i>
                      </CButton>
                    </template>
                    <CDropdownItem
                      class="px-2 py-1"
                      @click="markAsReadUnread(item)"
                    >
                      <i class="fas fa-check mr-2"></i>
                      {{
                        item.Status == $const.NOTICE_STATUS.New
                          ? "Mark as read"
                          : "Mark as unread"
                      }}
                    </CDropdownItem>
                    <CDropdownItem
                      class="px-2 py-1"
                      @click="removeNotice(item)"
                    >
                      <i class="fas fa-trash mr-2"></i> Remove
                    </CDropdownItem>
                  </CDropdown>
                </div>
              </div>
            </div>
          </div>
        </template>

        <Pagination
          class="mt-3 mb-0"
          :pagingInfo="{
            page: queryObject.page,
            pageSize: queryObject.pageSize,
            total,
          }"
          @page="navigatePage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from "@/components/pagination.vue";
export default {
  name: "Notice",
  components: {
    Pagination,
  },
  data() {
    return {
      loading: false,
      total: 0,
      items: [],
      hasNewNotice: false,
    };
  },
  computed: {
    queryObject() {
      let val = this.$route.query ?? {};
      return {
        page: val.page ?? 1,
        pageSize: val.pageSize ?? 20,
      };
    },
    sipUser() {
      let sipUser = localStorage.getItem("sipUser");
      if (sipUser) {
        return JSON.parse(sipUser);
      } else {
        return null;
      }
    },
  },
  created: function () {
    this.$messageHub.$on("UpdatedNotice", this.updateNotice);
  },
  beforeDestroy: function () {
    //clean SignalR event
    this.$messageHub.$off("UpdatedNotice", this.updateNotice);
  },
  mounted() {
    this.loadItems(this.queryObject);
  },
  watch: {
    queryObject(val) {
      this.loadItems(val);
    },
  },
  methods: {
    async loadItems(queryObj) {
      this.loading = true;

      try {
        let filter = [];

        let params = {
          $top: queryObj ? queryObj.pageSize : 20,
          $count: true,
          $orderby: "CreatedAt desc",
          $select: "Id,EntityName,EntityId,ActionType,Status,CreatedAt",
          $skip: queryObj ? (queryObj.page - 1) * queryObj.pageSize : 0,
          $expand: `CreatedByNavigation($select=FirstName,Email)`,
        };

        if (filter && filter.length) {
          params.$filter = filter.join(` and `);
        }

        let resp = await this.$http.get(`odata/SipNotice`, {
          params,
        });
        if (resp && resp.status == 200) {
          this.total = resp.data ? resp.data["@odata.count"] : 0;
          this.items =
            resp.data.value && resp.data.value.length ? resp.data.value : [];
        } else {
          this.total = 0;
          this.items = [];
        }
      } catch (error) {
        alert(error);
      }

      this.loading = false;
    },

    reloadPage() {
      let queryObj = this.queryObject;
      if (queryObj && queryObj.page > 1) {
        let query = {
          page: 1,
          pageSize: queryObj.pageSize,
        };

        this.$router
          .push({
            name: "Notice",
            query,
            //hash: null,
          })
          .catch(() => {});
      } else {
        this.loadItems(queryObj);
      }
      this.hasNewNotice = false;
    },

    navigatePage(page) {
      if (this.queryObject.page != page) {
        let query = {
          page: page,
          pageSize: this.queryObject.pageSize,
        };

        this.$router
          .push({
            name: "Notice",
            query,
            //hash: null,
          })
          .catch(() => {});
      }
    },

    replaceByDefault(e) {
      e.target.src = "/img/avatar.png";
    },

    showNoticeText(notice) {
      let result = "";

      let firstName = notice.CreatedByNavigation.FirstName;
      let objName = notice.EntityName
        ? notice.EntityName.toLowerCase()
        : "post";

      if (notice.ActionType == this.$const.SIP_ACTION_TYPES.Post) {
        result = `<strong>${firstName}</strong> has just generated new ${objName}.`;
      } else if (notice.ActionType == this.$const.SIP_ACTION_TYPES.Update) {
        result = `<strong>${firstName}</strong> has just updated his/her ${objName}.`;
      } else if (notice.ActionType == this.$const.SIP_ACTION_TYPES.Approve) {
        result = `<strong>${firstName}</strong> has just approved your ${objName}.`;
      } else if (notice.ActionType == this.$const.SIP_ACTION_TYPES.NeedsInfo) {
        result = `<strong>${firstName}</strong> needs more information about your ${objName}.`;
      } else if (notice.ActionType == this.$const.SIP_ACTION_TYPES.Reject) {
        result = `<strong>${firstName}</strong> has just declined your ${objName}.`;
      } else if (
        notice.ActionType == this.$const.SIP_ACTION_TYPES.AddSolution
      ) {
        result = `<strong>${firstName}</strong> has just added new solution in the ${objName}.`;
      } else if (
        notice.ActionType == this.$const.SIP_ACTION_TYPES.ApproveSolution
      ) {
        result = `<strong>${firstName}</strong> has just approved your solution for the ${objName}.`;
      } else if (
        notice.ActionType == this.$const.SIP_ACTION_TYPES.ChallengeSolved
      ) {
        result = `<strong>${firstName}</strong> has already solved your ${objName}.`;
      } else {
        result = `<strong>${firstName}</strong> has just done something with your ${objName}.`;
      }

      return result;
    },

    async clickNotice(item) {
      if (item.Status == this.$const.NOTICE_STATUS.New) {
        await this.$http.patch(`/odata/SipNotice/${item.Id}`, {
          Status: this.$const.NOTICE_STATUS.Read,
        });
        this.$user.dispatch("getNoticeCount");
      }
      let routerName =
        item.EntityName == "Idea"
          ? "IdeaDetail"
          : item.EntityName == "Challenge"
          ? "ChallengeDetail"
          : "";
      if (routerName) {
        this.$router.push({
          name: routerName,
          params: {
            id: item.EntityId,
          },
        });
      }
    },

    async markAsReadUnread(item) {
      await this.$http.patch(`/odata/SipNotice/${item.Id}`, {
        Status:
          item.Status == this.$const.NOTICE_STATUS.New
            ? this.$const.NOTICE_STATUS.Read
            : this.$const.NOTICE_STATUS.New,
      });
      item.Status =
        item.Status == this.$const.NOTICE_STATUS.New
          ? this.$const.NOTICE_STATUS.Read
          : this.$const.NOTICE_STATUS.New;
      this.$user.dispatch("getNoticeCount");
    },

    async removeNotice(item) {
      await this.$http.delete(`/odata/SipNotice/${item.Id}`);
      this.loadItems(this.queryObject);
      this.$user.dispatch("getNoticeCount");
    },

    async markAllAsRead() {
      let ids = [];
      let resp = await this.$http.get(`/odata/SipNotice`, {
        params: {
          $select: `Id`,
          $filter: `Status eq ${this.$const.NOTICE_STATUS.New}`,
        },
      });
      if (
        resp &&
        resp.status == 200 &&
        resp.data &&
        resp.data.value &&
        resp.data.value.length
      ) {
        ids = resp.data.value.map((_) => _.Id);
      }

      if (ids && ids.length) {
        await this.$func.asyncForEach(ids, async (id) => {
          await this.$http.patch(`/odata/SipNotice/${id}`, {
            Status: this.$const.NOTICE_STATUS.Read,
          });
        });
        this.$user.dispatch("getNoticeCount");
        this.reloadPage();
      }
    },

    updateNotice({ userId }) {
      if (this.sipUser && this.sipUser.UserId == userId) {
        this.hasNewNotice = true;
      }
    },

    showDateTime(datetime) {
      return this.$moment().diff(this.$moment(datetime), "weeks") < 1
        ? this.$moment(datetime).fromNow()
        : this.$moment(datetime).format("DD MMM YYYY, HH:mm");
    },
  },
};
</script>

<style lang="scss" scoped>
.notice-item {
  &:hover {
    font-weight: bold;
  }
}
</style>